/**
 * contactMerge — reconcile the migration's duplicate `astro_sync` Contacts.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 *  The astro-family SYNC (`reconcileAstroFamiliesIntoGroups`, familyStore.ts)
 *  minted a fresh `clients/{C#####}` Contact for every astro person, stamped
 *  `source:'astro_sync'` + `astroPersonId`. For people who ALREADY existed as
 *  front-door Contacts (e.g. "Rohit Khandelwal" C00506 shadows C00243), that
 *  created ~33 SHADOW duplicates. This tool folds each shadow onto its
 *  canonical (existing, non-astro_sync) Contact WITH full re-pointing, then
 *  soft-trashes the shadow. Owner-authorised 2026-07-02.
 *
 *  MATCHER REUSE — this module does NOT invent a matcher. It reuses the pure
 *  spine matcher `matchContacts()` from `contactMatch.ts` (the SSOT for
 *  "is this the same person?"). Because astro_sync shadows carry NO phone /
 *  email, the strongest signal that matcher can fire for them is `name-exact`
 *  (0.5, SUGGEST-tier) — never an auto-link. Per the matcher's owner-locked
 *  doctrine ("a false merge is worse than a miss"), a name-only hit is NOT
 *  enough to merge on its own. So the confident-merge gate additionally
 *  requires a corroborating birth-date (`dob`) match, or a phone/email-exact
 *  from the matcher. Name-only, un-corroborated hits go to `needsReview` for
 *  the owner to confirm — never auto-merged.
 *
 *  REVERSIBILITY — the shadow is SOFT-trashed (`trashed:true`, recoverable via
 *  the existing Trash view / `trashClient(..., false)`), never hard-deleted.
 *  The merge is audit-stamped (`mergedIntoContactId`, `astroPersonIdArchived`).
 *
 *  IDEMPOTENCY — a merged shadow is `trashed` and a kept shadow has `source`
 *  normalised off `astro_sync`; both drop out of the candidate set, so a
 *  second run finds 0 shadows and writes nothing. Every re-point is also
 *  guarded (a group/project is only written when it still references the
 *  shadow), so even a re-scan is a pure no-op.
 * ─────────────────────────────────────────────────────────────────────────
 *
 *  PURITY SEAM — `planReconcile()` is PURE (no Firestore): it takes the raw
 *  shadow / canonical / family / project docs and returns the full plan +
 *  resolved write-actions. `reconcileDuplicateContacts()` is the impure seam:
 *  it reads the three collections once, calls `planReconcile`, and (only when
 *  `dryRun:false`) applies the actions in chunked batches.
 */
import {
  arrayUnion,
  collection,
  deleteField,
  doc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import { matchContacts, type ContactLite, type MatchResult } from './contactMatch';

/* ═══════════════════════════════ Types ═════════════════════════════════════ */

/** The subset of a `clients/{cid}` doc this reconcile reads. */
export interface ContactDoc {
  cid: string;
  clientNo: string;
  name: string;
  source?: string;
  astroPersonId?: string;
  dob?: string;
  tob?: string;
  pob?: {
    name?: string;
    city?: string;
    state?: string;
    country?: string;
    lat?: number;
    /** Build A — `lon` is the current longitude key; `lng` is read for legacy docs only. */
    lon?: number;
    lng?: number;
    tz?: string;
  };
  phone?: string;
  email?: string;
  phones?: Array<{ number: string }>;
  emails?: Array<{ address: string }>;
  familyIds?: string[];
  trashed?: boolean;
}

/** The subset of a `families/{id}` (Group) doc this reconcile reads.
 *  The index signature preserves any extra member axes (work_role,
 *  department, relation_to_client, reports_to…) when a membership is
 *  re-pointed loser→survivor — a rename must never silently drop them. */
export interface FamilyDoc {
  id: string;
  members: Array<{ contactId: string; name: string; relationship?: string; [k: string]: unknown }>;
}

/** The subset of a `projects/{pid}` doc this reconcile / merge reads. */
export interface ProjectDoc {
  pid: string;
  clientId?: string;
  clientNo?: string;
  /** Display cache of the primary client's name — refreshed on a primary repoint. */
  clientName?: string;
  /** Channel partner link (nullable). A partner Contact can also be merged. */
  partnerId?: string | null;
  linked_contact_ids?: string[] | null;
  participants?: Array<{ contactId: string; [k: string]: unknown }>;
}

/** A resolved, ready-to-apply merge of one shadow onto its canonical. */
export interface MergeAction {
  dupeId: string;
  dupeName: string;
  canonicalId: string;
  canonicalName: string;
  /** Why this pair is confident (e.g. 'name+dob-exact', 'phone-exact'). */
  reason: string;
  /** Which astro fields will be blank-filled onto the canonical. */
  fieldsCarried: string[];
  /** Field patch to merge onto the canonical Contact (astro birth details). */
  carryPatch: Record<string, unknown>;
  /** Groups whose `members[]` will be rewritten (shadow → canonical). */
  groupUpdates: Array<{ id: string; newMembers: FamilyDoc['members'] }>;
  /** Projects whose `linked_contact_ids` will be rewritten. */
  projectLinkedUpdates: Array<{ pid: string; newIds: string[] }>;
  /** Projects whose `participants[]` will be rewritten. */
  projectParticipantUpdates: Array<{ pid: string; newParticipants: ProjectDoc['participants'] }>;
}

export interface ReviewItem {
  dupeId: string;
  dupeName: string;
  candidateId?: string;
  candidateName?: string;
  reason: string;
  /** The birth details the shadow carries — shown so the owner can eyeball. */
  dupeBirth?: { dob?: string; tob?: string; pob?: string };
}

export interface KeepItem {
  dupeId: string;
  dupeName: string;
}

export interface ReconcilePlan {
  counts: { willMerge: number; willKeep: number; needsReview: number };
  merges: MergeAction[];
  keeps: KeepItem[];
  needsReview: ReviewItem[];
  /** Unexpected: a shadow referenced as a project's PRIMARY client. Flagged, not auto-handled. */
  warnings: { clientIdRefs: Array<{ dupeId: string; pid: string }> };
  /** Total astro_sync shadows scanned this run. */
  scannedDupes: number;
  /** true → this plan was applied (dryRun:false). */
  applied: boolean;
}

/* ═══════════════════════════════ Pure helpers ══════════════════════════════ */

function nonBlank(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/** Does this Contact have a MEANINGFUL place-of-birth (real content/coords)? */
function hasPob(c: ContactDoc): boolean {
  const p = c.pob;
  if (!p) return false;
  const hasText = nonBlank(p.city) || nonBlank(p.name) || nonBlank(p.state) || nonBlank(p.country);
  const hasCoords =
    (typeof p.lat === 'number' && p.lat !== 0) ||
    // Build A — `lon` is current; `lng` is still read for legacy docs.
    (typeof p.lon === 'number' && p.lon !== 0) ||
    (typeof p.lng === 'number' && p.lng !== 0);
  return hasText || hasCoords;
}

/** Build the pure-matcher input for a shadow Contact. */
function inputForDupe(d: ContactDoc): { name: string; phone: string; email: string } {
  const phone = d.phones?.[0]?.number || d.phone || '';
  const email = d.emails?.[0]?.address || d.email || '';
  return { name: d.name, phone, email };
}

/** Map a ContactDoc to the pure matcher's ContactLite shape. */
function toLite(c: ContactDoc): ContactLite {
  return {
    cid: c.cid,
    clientNo: c.clientNo,
    name: c.name,
    phone: c.phone,
    email: c.email,
    phones: c.phones,
    emails: c.emails,
  };
}

/**
 * The confident-merge gate. Name-exact ALONE (the only signal an astro_sync
 * shadow can fire) is SUGGEST-tier — it must be corroborated by a birth-date
 * match (or a phone/email-exact) to be safe to auto-merge. Returns the merge
 * reason when confident, or a review reason when not.
 */
export function confirmSamePerson(
  dupe: ContactDoc,
  canonical: ContactDoc,
  top: MatchResult,
): { confirmed: boolean; reason: string } {
  if (top.reasons.includes('phone-exact')) return { confirmed: true, reason: 'phone-exact' };
  if (top.reasons.includes('email-exact')) return { confirmed: true, reason: 'email-exact' };
  // Name-exact path — require a corroborating birth-date.
  const d = (dupe.dob ?? '').trim();
  const c = (canonical.dob ?? '').trim();
  if (d && c) {
    return d === c
      ? { confirmed: true, reason: 'name+dob-exact' }
      : { confirmed: false, reason: 'name matches but birth dates differ' };
  }
  return {
    confirmed: false,
    reason: c
      ? 'name-only (shadow carries no birth date to confirm)'
      : 'name-only (canonical has no birth date to confirm)',
  };
}

/**
 * PURE — compute the full reconcile plan. No Firestore. Takes every astro_sync
 * shadow, the canonical (non-shadow, non-trashed) Contacts, and the FULL
 * families + projects lists (scanned exhaustively so no reference is missed).
 */
export function planReconcile(
  dupes: ContactDoc[],
  canonicals: ContactDoc[],
  families: FamilyDoc[],
  projects: ProjectDoc[],
): Omit<ReconcilePlan, 'applied'> {
  const canonicalLite = canonicals.map(toLite);
  const byId = new Map<string, ContactDoc>(canonicals.map((c) => [c.cid, c]));

  const merges: MergeAction[] = [];
  const keeps: KeepItem[] = [];
  const needsReview: ReviewItem[] = [];
  const clientIdRefs: Array<{ dupeId: string; pid: string }> = [];

  for (const dupe of dupes) {
    const results = matchContacts(inputForDupe(dupe), canonicalLite);

    // 3. No match → keep (normalise source off astro_sync at execute time).
    if (results.length === 0) {
      keeps.push({ dupeId: dupe.cid, dupeName: dupe.name });
      continue;
    }

    const top = results[0];
    const dupeBirth = {
      dob: dupe.dob,
      tob: dupe.tob,
      pob: dupe.pob?.name || dupe.pob?.city,
    };

    // Ambiguity guard: when the top hit is name-only, more than one contact
    // sharing that name is ambiguous (which one is the real person?).
    const nameOnly =
      top.reasons.includes('name-exact') &&
      !top.reasons.includes('phone-exact') &&
      !top.reasons.includes('email-exact');
    if (nameOnly) {
      const nameHits = results.filter((r) => r.reasons.includes('name-exact'));
      if (nameHits.length > 1) {
        needsReview.push({
          dupeId: dupe.cid,
          dupeName: dupe.name,
          reason: `${nameHits.length} contacts share this name — cannot pick one`,
          dupeBirth,
        });
        continue;
      }
    }

    const canonical = byId.get(top.contactId);
    if (!canonical) {
      // Defensive — should never happen (top.contactId came from canonicals).
      needsReview.push({
        dupeId: dupe.cid,
        dupeName: dupe.name,
        candidateId: top.contactId,
        candidateName: top.name,
        reason: 'internal: candidate not found',
        dupeBirth,
      });
      continue;
    }

    // 4. Confident-merge gate.
    const verdict = confirmSamePerson(dupe, canonical, top);
    if (!verdict.confirmed) {
      needsReview.push({
        dupeId: dupe.cid,
        dupeName: dupe.name,
        candidateId: canonical.cid,
        candidateName: canonical.name,
        reason: verdict.reason,
        dupeBirth,
      });
      continue;
    }

    // 2. Build the MERGE action.
    // 2a. Astro birth details — blank-fill onto the canonical only.
    const carryPatch: Record<string, unknown> = {};
    const fieldsCarried: string[] = [];
    if (!nonBlank(canonical.dob) && nonBlank(dupe.dob)) {
      carryPatch.dob = dupe.dob;
      fieldsCarried.push('dob');
    }
    if (!nonBlank(canonical.tob) && nonBlank(dupe.tob)) {
      carryPatch.tob = dupe.tob;
      fieldsCarried.push('tob');
    }
    if (!hasPob(canonical) && hasPob(dupe)) {
      carryPatch.pob = dupe.pob;
      fieldsCarried.push('pob');
    }
    if (!nonBlank(canonical.astroPersonId) && nonBlank(dupe.astroPersonId)) {
      carryPatch.astroPersonId = dupe.astroPersonId;
      fieldsCarried.push('astroPersonId');
    }

    // 2b. Groups — re-point every membership (guarded: only groups that hold the shadow).
    const groupUpdates: MergeAction['groupUpdates'] = [];
    for (const fam of families) {
      if (!fam.members.some((m) => m.contactId === dupe.cid)) continue;
      const hasCanonical = fam.members.some((m) => m.contactId === canonical.cid);
      const newMembers = hasCanonical
        ? fam.members.filter((m) => m.contactId !== dupe.cid)
        : fam.members.map((m) =>
            m.contactId === dupe.cid
              ? {
                  contactId: canonical.cid,
                  name: canonical.name,
                  ...(m.relationship ? { relationship: m.relationship } : {}),
                }
              : m,
          );
      groupUpdates.push({ id: fam.id, newMembers });
    }

    // 2c. Projects — re-point linked_contact_ids + participants (guarded).
    const projectLinkedUpdates: MergeAction['projectLinkedUpdates'] = [];
    const projectParticipantUpdates: MergeAction['projectParticipantUpdates'] = [];
    for (const proj of projects) {
      const ids = proj.linked_contact_ids ?? [];
      if (ids.includes(dupe.cid)) {
        const newIds = Array.from(
          new Set(ids.map((id) => (id === dupe.cid ? canonical.cid : id))),
        );
        projectLinkedUpdates.push({ pid: proj.pid, newIds });
      }
      const parts = proj.participants ?? [];
      if (parts.some((p) => p.contactId === dupe.cid)) {
        const hasCanon = parts.some((p) => p.contactId === canonical.cid);
        const newParticipants = hasCanon
          ? parts.filter((p) => p.contactId !== dupe.cid)
          : parts.map((p) => (p.contactId === dupe.cid ? { ...p, contactId: canonical.cid } : p));
        projectParticipantUpdates.push({ pid: proj.pid, newParticipants });
      }
      // Unexpected: shadow as a project's PRIMARY client — flag, don't auto-touch.
      if ((proj.clientId || proj.clientNo) === dupe.cid) {
        clientIdRefs.push({ dupeId: dupe.cid, pid: proj.pid });
      }
    }

    merges.push({
      dupeId: dupe.cid,
      dupeName: dupe.name,
      canonicalId: canonical.cid,
      canonicalName: canonical.name,
      reason: verdict.reason,
      fieldsCarried,
      carryPatch,
      groupUpdates,
      projectLinkedUpdates,
      projectParticipantUpdates,
    });
  }

  return {
    counts: { willMerge: merges.length, willKeep: keeps.length, needsReview: needsReview.length },
    merges,
    keeps,
    needsReview,
    warnings: { clientIdRefs },
    scannedDupes: dupes.length,
  };
}

/* ═══════════════════════════════ Impure seam ═══════════════════════════════ */

/** Read one Firestore collection into typed docs (id + selected fields). */
async function readCollection<T>(
  name: string,
  map: (id: string, data: Record<string, unknown>) => T,
): Promise<T[]> {
  const snap = await getDocs(collection(db, name));
  return snap.docs.map((d) => map(d.id, d.data() as Record<string, unknown>));
}

/**
 * A chunked writeBatch runner. Firestore caps a batch at 500 ops; we commit at
 * 450 to stay well clear (each merge is a handful of ops, ~33 merges is tiny —
 * but chunking keeps this correct if the shadow count ever grows).
 */
class BatchRunner {
  private batch = writeBatch(db);
  private ops = 0;
  private readonly limit = 450;

  private async maybeFlush(): Promise<void> {
    if (this.ops >= this.limit) {
      await this.batch.commit();
      this.batch = writeBatch(db);
      this.ops = 0;
    }
  }

  async set(path: [string, string], data: Record<string, unknown>): Promise<void> {
    this.batch.set(doc(db, path[0], path[1]), data, { merge: true });
    this.ops += 1;
    await this.maybeFlush();
  }

  async update(path: [string, string], data: Record<string, unknown>): Promise<void> {
    this.batch.update(doc(db, path[0], path[1]), data);
    this.ops += 1;
    await this.maybeFlush();
  }

  async commit(): Promise<void> {
    if (this.ops > 0) await this.batch.commit();
  }
}

/**
 * Reconcile the migration's duplicate `astro_sync` Contacts.
 *
 * `dryRun:true`  → compute + return the full plan; NO writes.
 * `dryRun:false` → apply the plan (merge + re-point + soft-trash) in chunked
 *                  batches; return the same plan with `applied:true`.
 */
export async function reconcileDuplicateContacts(opts: {
  dryRun: boolean;
  actorName?: string;
}): Promise<ReconcilePlan> {
  const actorName = opts.actorName?.trim() || 'system';

  // 1. Read the three collections ONCE. Full scans are the exhaustive, safe
  //    strategy on this small dataset (mirrors reconcileAstroFamiliesIntoGroups)
  //    — members[] is a nested array of maps that can't be queried by
  //    contactId, and linked_contact_ids re-points must dedup, so a
  //    where(array-contains) query would still miss cases. Scan everything.
  const contacts = await readCollection<ContactDoc>('clients', (id, d) => ({
    cid: id,
    clientNo: typeof d.clientNo === 'string' ? d.clientNo : id,
    name: typeof d.name === 'string' ? d.name : '',
    source: typeof d.source === 'string' ? d.source : undefined,
    astroPersonId: typeof d.astroPersonId === 'string' ? d.astroPersonId : undefined,
    dob: typeof d.dob === 'string' ? d.dob : undefined,
    tob: typeof d.tob === 'string' ? d.tob : undefined,
    pob: d.pob && typeof d.pob === 'object' ? (d.pob as ContactDoc['pob']) : undefined,
    phone: typeof d.phone === 'string' ? d.phone : undefined,
    email: typeof d.email === 'string' ? d.email : undefined,
    phones: Array.isArray(d.phones) ? (d.phones as ContactDoc['phones']) : undefined,
    emails: Array.isArray(d.emails) ? (d.emails as ContactDoc['emails']) : undefined,
    familyIds: Array.isArray(d.familyIds) ? (d.familyIds as string[]) : undefined,
    trashed: d.trashed === true,
  }));

  const families = await readCollection<FamilyDoc>('families', (id, d) => ({
    id,
    members: Array.isArray(d.members) ? (d.members as FamilyDoc['members']) : [],
  }));

  const projects = await readCollection<ProjectDoc>('projects', (id, d) => ({
    pid: id,
    clientId: typeof d.clientId === 'string' ? d.clientId : undefined,
    clientNo: typeof d.clientNo === 'string' ? d.clientNo : undefined,
    linked_contact_ids: Array.isArray(d.linked_contact_ids)
      ? (d.linked_contact_ids as unknown[]).filter((x): x is string => typeof x === 'string')
      : null,
    participants: Array.isArray(d.participants)
      ? (d.participants as ProjectDoc['participants'])
      : undefined,
  }));

  // 2. Partition into shadows (source:'astro_sync', has astroPersonId, live) vs
  //    canonicals (everything else that is live). A trashed/normalised shadow
  //    is already reconciled — excluded here → idempotent re-runs.
  const dupes = contacts.filter(
    (c) => c.source === 'astro_sync' && nonBlank(c.astroPersonId) && !c.trashed,
  );
  const canonicals = contacts.filter((c) => c.source !== 'astro_sync' && !c.trashed);

  // 3. Compute the plan (pure).
  const plan = planReconcile(dupes, canonicals, families, projects);

  if (opts.dryRun) {
    return { ...plan, applied: false };
  }

  // 4. Apply. One chunked runner across all actions.
  const runner = new BatchRunner();
  const now = serverTimestamp();
  const dupeById = new Map(contacts.map((c) => [c.cid, c]));

  for (const m of plan.merges) {
    // 4a. Canonical: astro blank-fill + familyIds denorm, one merge-set.
    const groupIds = m.groupUpdates.map((g) => g.id);
    const canonicalWrite: Record<string, unknown> = { ...m.carryPatch };
    if (groupIds.length > 0) canonicalWrite.familyIds = arrayUnion(...groupIds);
    if (Object.keys(canonicalWrite).length > 0) {
      canonicalWrite.updatedAt = now;
      canonicalWrite.updatedBy = actorName;
      await runner.set(['clients', m.canonicalId], canonicalWrite);
    }

    // 4b. Groups → rewrite members[].
    for (const g of m.groupUpdates) {
      await runner.update(['families', g.id], {
        members: g.newMembers,
        updatedAt: now,
        updatedBy: actorName,
      });
    }

    // 4c. Projects → rewrite linked_contact_ids + participants.
    for (const p of m.projectLinkedUpdates) {
      await runner.update(['projects', p.pid], {
        linked_contact_ids: p.newIds,
        updatedAt: now,
      });
    }
    for (const p of m.projectParticipantUpdates) {
      await runner.update(['projects', p.pid], {
        participants: p.newParticipants,
        updatedAt: now,
      });
    }

    // 4d. Soft-trash the shadow with full audit; move the astro key so the
    //     live dedup index (astroPersonId) stays unique on the canonical.
    const dupe = dupeById.get(m.dupeId);
    await runner.set(['clients', m.dupeId], {
      trashed: true,
      trashedBy: actorName,
      trashedAt: now,
      updatedBy: actorName,
      updatedAt: now,
      source: 'astro',
      mergedIntoContactId: m.canonicalId,
      astroPersonIdArchived: dupe?.astroPersonId ?? null,
      astroPersonId: deleteField(),
      familyIds: [],
    });
  }

  // 4e. Kept shadows: normalise source off astro_sync (no other change).
  for (const k of plan.keeps) {
    await runner.set(['clients', k.dupeId], {
      source: 'astro',
      updatedBy: actorName,
      updatedAt: now,
    });
  }

  await runner.commit();
  return { ...plan, applied: true };
}

/* ═══════════ Generic staff-initiated contact merge (GAP 1) ═══════════════════
 *
 *  WHY THIS EXISTS
 *  The Duplicates tab lets a human merge two Contacts field-by-field. Applying
 *  the chosen field VALUES is the UI's job (name / phones / emails / personData
 *  / roles are ordinary survivor writes). What was MISSING is the reference
 *  re-point: the loser can still be a project's participant / linked contact /
 *  PRIMARY client / channel partner, and a group member. Until every such
 *  reference moves to the survivor, soft-trashing the loser would orphan its
 *  work (and `clientTrashBlock` rightly refuses).
 *
 *  `planContactMergeRepoint` is the PURE generalisation of the per-shadow
 *  re-point loop inside `planReconcile` (participants + linked_contact_ids +
 *  group members), EXTENDED to also move the legacy primary-client
 *  (`clientId`/`clientNo`/`clientName`) and `partnerId` fields that
 *  `planReconcile` only FLAGGED as a warning. `mergeContactInto` is the impure
 *  seam the Contacts UI calls: it reads projects + families once, plans, then
 *  applies every re-point AND the loser soft-trash in a SINGLE atomic batch.
 *
 *  ATOMICITY — realistic op counts (a Contact in a handful of projects/groups)
 *  fit one Firestore batch, so the whole merge is all-or-nothing. The loser
 *  soft-trash is queued LAST, so if any project/group write fails the batch
 *  aborts BEFORE the loser is trashed — the merge never half-lands with an
 *  orphaned loser.
 *
 *  IDEMPOTENCY — every re-point is guarded (a project/group is only rewritten
 *  while it still references the loser). After a successful merge the loser
 *  holds no references, so a re-run plans zero project/group writes and the
 *  soft-trash re-set is a no-op. Safe to re-run.
 * ────────────────────────────────────────────────────────────────────────── */

/** A resolved, ready-to-apply re-point of one loser Contact onto a survivor. */
export interface ContactMergeRepoint {
  /** Projects to rewrite, each with its exact field patch (loser→survivor). */
  projectUpdates: Array<{ pid: string; patch: Record<string, unknown> }>;
  /** Groups whose `members[]` are rewritten (loser membership → survivor / dropped). */
  groupUpdates: Array<{ id: string; newMembers: FamilyDoc['members'] }>;
  /** Group ids the survivor GAINS — arrayUnion onto `survivor.familyIds`. */
  survivorGainsGroupIds: string[];
  counts: { projects: number; groups: number };
}

/**
 * PURE — compute the full loser→survivor re-point. No Firestore. Takes the
 * loser + survivor ids, the survivor's (chosen) display name, and the FULL
 * projects + families lists (scanned exhaustively so no reference is missed).
 *
 * `moveGroups` (default true) mirrors the UI's "move group memberships onto the
 * survivor" pick: when false the loser is simply DROPPED from each group it
 * shares (the survivor does not inherit those memberships).
 */
export function planContactMergeRepoint(
  loserId: string,
  survivorId: string,
  survivorName: string,
  projects: ProjectDoc[],
  families: FamilyDoc[],
  opts?: { moveGroups?: boolean },
): ContactMergeRepoint {
  const moveGroups = opts?.moveGroups !== false;
  const projectUpdates: ContactMergeRepoint['projectUpdates'] = [];

  for (const proj of projects) {
    const patch: Record<string, unknown> = {};

    // Participants — re-point (dedup if the survivor is already a participant).
    const parts = proj.participants ?? [];
    if (parts.some((p) => p.contactId === loserId)) {
      const hasSurvivor = parts.some((p) => p.contactId === survivorId);
      patch.participants = hasSurvivor
        ? parts.filter((p) => p.contactId !== loserId)
        : parts.map((p) => (p.contactId === loserId ? { ...p, contactId: survivorId } : p));
    }

    // linked_contact_ids — re-point + dedup.
    const ids = proj.linked_contact_ids ?? [];
    if (ids.includes(loserId)) {
      patch.linked_contact_ids = Array.from(
        new Set(ids.map((id) => (id === loserId ? survivorId : id))),
      );
    }

    // Primary client — `clientId` is the SSOT link, `clientNo` its display
    // cache (they must never diverge); move them together and refresh the
    // cached `clientName`. This is what clears `clientTrashBlock` on the loser.
    if (proj.clientId === loserId || proj.clientNo === loserId) {
      patch.clientId = survivorId;
      patch.clientNo = survivorId;
      if (nonBlank(survivorName)) patch.clientName = survivorName;
    }

    // Channel partner — a partner Contact merged into another.
    if (proj.partnerId === loserId) {
      patch.partnerId = survivorId;
    }

    if (Object.keys(patch).length > 0) projectUpdates.push({ pid: proj.pid, patch });
  }

  const groupUpdates: ContactMergeRepoint['groupUpdates'] = [];
  const survivorGainsGroupIds: string[] = [];

  for (const fam of families) {
    if (!fam.members.some((m) => m.contactId === loserId)) continue;
    const hasSurvivor = fam.members.some((m) => m.contactId === survivorId);
    let newMembers: FamilyDoc['members'];
    if (!moveGroups || hasSurvivor) {
      // Survivor already a member, or we are not moving membership → drop loser.
      newMembers = fam.members.filter((m) => m.contactId !== loserId);
    } else {
      // Rename the loser membership to the survivor, PRESERVING every other
      // axis on that member (work_role/department/relation_to_client/…).
      newMembers = fam.members.map((m) =>
        m.contactId === loserId
          ? { ...m, contactId: survivorId, name: nonBlank(survivorName) ? survivorName : m.name }
          : m,
      );
      survivorGainsGroupIds.push(fam.id);
    }
    groupUpdates.push({ id: fam.id, newMembers });
  }

  return {
    projectUpdates,
    groupUpdates,
    survivorGainsGroupIds,
    counts: { projects: projectUpdates.length, groups: groupUpdates.length },
  };
}

/** Read the `projects` collection into the ProjectDoc subset this merge needs. */
async function readProjectsForMerge(): Promise<ProjectDoc[]> {
  return readCollection<ProjectDoc>('projects', (id, d) => ({
    pid: id,
    clientId: typeof d.clientId === 'string' ? d.clientId : undefined,
    clientNo: typeof d.clientNo === 'string' ? d.clientNo : undefined,
    clientName: typeof d.clientName === 'string' ? d.clientName : undefined,
    partnerId: typeof d.partnerId === 'string' ? d.partnerId : null,
    linked_contact_ids: Array.isArray(d.linked_contact_ids)
      ? (d.linked_contact_ids as unknown[]).filter((x): x is string => typeof x === 'string')
      : null,
    participants: Array.isArray(d.participants)
      ? (d.participants as ProjectDoc['participants'])
      : undefined,
  }));
}

/**
 * Merge one Contact (`loserId`) INTO another (`survivorId`): re-point every
 * project + group reference from loser→survivor — plus the direct contact FKs
 * that resolve independently of the project (`quotes.clientId`, `leads.contactId`)
 * — then soft-trash the loser, all in ONE atomic batch (soft-trash queued last,
 * so a write failure aborts before the loser is trashed). Returns the project /
 * group re-point counts.
 *
 * The caller (Contacts.tsx `doMerge`) applies the chosen field VALUES to the
 * survivor first (name/phones/emails/personData/roles), then calls this for the
 * reference-repoint + soft-trash — the cleaner seam that keeps the field-by-field
 * picks honoured while this owns the data-integrity-critical re-pointing.
 *
 * Guards: refuses a self-merge, and a missing loser/survivor id.
 */
export async function mergeContactInto(
  loserId: string,
  survivorId: string,
  actorName: string,
  opts?: { survivorName?: string; moveGroups?: boolean },
): Promise<{ projectsRepointed: number; groupsRepointed: number }> {
  const actor = actorName?.trim() || 'system';
  if (!loserId || !survivorId) {
    throw new Error('Merge needs both a loser and a survivor contact.');
  }
  if (loserId === survivorId) {
    throw new Error('Cannot merge a contact into itself.');
  }

  // Read projects + families once (full scans — the same exhaustive, safe
  // strategy planReconcile uses: members[] and linked_contact_ids re-points
  // can't be array-queried without missing dedup cases).
  const projects = await readProjectsForMerge();
  const families = await readCollection<FamilyDoc>('families', (id, d) => ({
    id,
    members: Array.isArray(d.members) ? (d.members as FamilyDoc['members']) : [],
  }));

  const survivorName = opts?.survivorName?.trim() || '';
  const plan = planContactMergeRepoint(loserId, survivorId, survivorName, projects, families, {
    moveGroups: opts?.moveGroups !== false,
  });

  const runner = new BatchRunner();
  const now = serverTimestamp();

  // 1. Project re-points.
  for (const u of plan.projectUpdates) {
    await runner.update(['projects', u.pid], { ...u.patch, updatedAt: now });
  }
  // 2. Group member rewrites.
  for (const g of plan.groupUpdates) {
    await runner.update(['families', g.id], {
      members: g.newMembers,
      updatedAt: now,
      updatedBy: actor,
    });
  }
  // 3. Survivor gains the loser's group ids (familyIds denorm) — only when moving.
  if (plan.survivorGainsGroupIds.length > 0) {
    await runner.set(['clients', survivorId], {
      familyIds: arrayUnion(...plan.survivorGainsGroupIds),
      updatedAt: now,
      updatedBy: actor,
    });
  }
  // 3b. DIRECT contact FKs that resolve INDEPENDENTLY of the project (Vex LOW):
  //     · quotes.clientId — a quote's stamped recipient identity;
  //       `resolveQuoteClientId` SHORT-CIRCUITS on it, so a stale value would
  //       route a sent quote / pay link to the trashed loser even after the
  //       project's clientId moved. Re-point it.
  //     · leads.contactId — a lead's matched-contact link. Re-point it.
  //     payments are EXCLUDED: they are queried only by `projectId`
  //     (recordPaymentStore), so they follow the project we already re-pointed;
  //     the clientId persisted on a payment is a display cache nothing queries.
  const quoteSnap = await getDocs(
    query(collection(db, 'quotes'), where('clientId', '==', loserId)),
  );
  for (const d of quoteSnap.docs) {
    await runner.update(['quotes', d.id], { clientId: survivorId, updatedAt: now });
  }
  const leadSnap = await getDocs(
    query(collection(db, 'leads'), where('contactId', '==', loserId)),
  );
  for (const d of leadSnap.docs) {
    await runner.update(['leads', d.id], { contactId: survivorId, updatedAt: now });
  }
  // 4. Soft-trash the loser LAST, with the contact soft-delete body + a merge
  //    audit stamp; familyIds cleared so it drops off every contact card.
  await runner.set(['clients', loserId], {
    trashed: true,
    trashedBy: actor,
    trashedAt: now,
    updatedBy: actor,
    updatedAt: now,
    mergedIntoContactId: survivorId,
    familyIds: [],
  });

  await runner.commit();
  return { projectsRepointed: plan.counts.projects, groupsRepointed: plan.counts.groups };
}
